import React from "react";
import {
  RiseOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button } from "./ui";
import type { DashboardInsight } from "../store/dashboardSlice";

const ICON_MAP: Record<string, React.ReactElement> = {
  TrendingUp: <RiseOutlined />,
  CheckCircle2: <CheckCircleOutlined />,
  CircleDashed: <ClockCircleOutlined />,
  CalendarClock: <CalendarOutlined />,
  AlertTriangle: <WarningOutlined />,
};

const DashboardSummaryHeader = ({
  onCreateDream,
  insights = [],
  loading = false,
  hasDreams = false,
}: {
  onCreateDream: () => void;
  insights: DashboardInsight[];
  loading?: boolean;
  hasDreams?: boolean;
}) => {
  return (
    <section
      className="rounded-2xl bg-brand-overlay p-6"
      data-testid="dashboard-summary-header"
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold leading-none text-slate-900 md:text-xl">
          Dreams Dashboard
        </h2>
        {hasDreams && (
          <>
            {/* Mobile: icon-only compact button stays on same row as title */}
            <Button
              variant="primary"
              size="sm"
              onClick={onCreateDream}
              aria-label="Create a new dream"
              data-testid="dashboard-summary-new-dream-button"
              className="sm:hidden"
            >
              +
            </Button>
            {/* Desktop: full button */}
            <Button
              variant="primary"
              size="md"
              onClick={onCreateDream}
              aria-label="Create a new dream"
              className="hidden sm:flex"
            >
              + New dream
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1.5 sm:gap-3 md:grid-cols-3 xl:grid-cols-5">
        {loading && insights.length === 0
          ? Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-[62px] animate-pulse rounded-lg border border-slate-200 bg-white"
              />
            ))
          : insights.map((insight) => (
              <article
                key={insight.key}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 sm:gap-3 sm:px-4 sm:py-3"
                data-testid={`dashboard-summary-stat-${insight.key}`}
              >
                <span
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-overlay text-sm text-brand-primary sm:h-7 sm:w-7 sm:text-base"
                  aria-hidden="true"
                >
                  {ICON_MAP[insight.icon] ?? <RiseOutlined />}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-bold tracking-wide text-slate-400">
                    {insight.label.toUpperCase()}
                  </p>
                  <p className="text-base font-semibold leading-none text-slate-900 sm:text-2xl">
                    {insight.value}
                  </p>
                </div>
              </article>
            ))}
      </div>
    </section>
  );
};

export default DashboardSummaryHeader;
